import React, { useState, useEffect } from "react";
import { Search, Plus, X, Trash2 } from "lucide-react";
import MemberDetails from "./MemberDetails"; // Ensure this component is available
import avatar from "../../assets/3d_avatar_26.png";
import { toast } from "sonner";
import { BASE_URL } from "utils/constant";
import { useAlert } from "../../components/UI_components/Alert"; // Import the alert hook

const AddNewMember = ({ onClose, organizationId }) => {
  const [emailInput, setEmailInput] = useState("");
  const [addedMembers, setAddedMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  // Get the alert function from our context
  const { addAlert } = useAlert();

  async function getUserRole() {
    try {
      const accessToken = localStorage.getItem("access");

      if (accessToken) {
        const response = await fetch(`${BASE_URL}/auth/user-details-of-user/`, {
          method: "GET",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
        });

        const data = await response.json();
        if (data?.status) {
          setUserData(data?.data);
        } else {
          return;
        }
      }
    } catch (error) {
      addAlert("Failed to get user details", "error");
    }
  }

  useEffect(() => {
    getUserRole();
  }, []);

  const getOrganizationMembers = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      if (accessToken && organizationId) {
        const response = await fetch(
          `${BASE_URL}/org/organization/${organizationId}/members/`,
          {
            method: "GET",
            headers: new Headers({
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            }),
          }
        );
        const data = await response.json();
        if (data.status) {
          // console.log("Organization members:", data.data);
          setAddedMembers(data.data);
        }
      }
    } catch (error) {
      // console.log("Error fetching organization members:", error);
      addAlert("Failed to fetch organization members", "error");
    }
  };

  useEffect(() => {
    getOrganizationMembers();
  }, [organizationId]);

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        setLoading(false);
        return;
      }

      const response = await fetch(`${BASE_URL}/auth/search-users/`, {
        method: "GET",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        }),
      });

      const result = await response.json();
      if (!result?.status) {
        addAlert(result?.message || "Failed to fetch users", "error");
        setLoading(false);
        return;
      }

      setAllUsers(result?.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      addAlert("Failed to fetch users", "error");
    }
  };

  const addMemberToOrganization = async (user) => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${BASE_URL}/org/organization/${organizationId}/add-member/`,
        {
          method: "POST",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
          body: JSON.stringify({
            organization: organizationId,
            user_id: user?.user_id,
            is_admin: false,
            member_control: false,
            joined_as: "member",
            created_by: userData?.first_name,
            last_updated_by: userData?.first_name,
          }),
        }
      );

      const result = await response.json();
      if (!result?.status) {
        addAlert(result?.message || "Cannot add an owner of organisation", "error");
        setLoading(false);
        return;
      }

      getOrganizationMembers();
      setEmailInput("");
      setFilteredUsers([]);
      addAlert("Member added successfully", "success");
      setLoading(false);
    } catch (error) {
      setLoading(false);
      addAlert("Cannot add an owner of organisation", "error");
    }
  };

  // Function to initiate member deletion with confirmation
  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowConfirmation(true);
  };

  // Function to cancel delete operation
  const cancelDelete = () => {
    setShowConfirmation(false);
    setMemberToDelete(null);
  };

  // Function to confirm and execute member deletion
  const confirmDelete = async () => {
    if (!memberToDelete) return;

    setLoading(true);
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        setLoading(false);
        setShowConfirmation(false);
        return;
      }

      const response = await fetch(
        `${BASE_URL}/org/organization/member/${memberToDelete.id}/delete/`,
        {
          method: "DELETE",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
        }
      );

      // Check if response is ok (status in 200-299 range)
      if (response.ok) {
        addAlert("Member removed successfully", "success");
        getOrganizationMembers(); // Refresh the members list
      } else {
        const errorData = await response.json();
        addAlert(errorData?.message || "Failed to remove member", "error");
      }
    } catch (error) {
      addAlert("Error removing member", "error");
      console.error("Error removing member:", error);
    } finally {
      setLoading(false);
      setShowConfirmation(false);
      setMemberToDelete(null);
    }
  };

  const handleInputChange = (e) => {
    setEmailInput(e.target.value);
    const filtered = allUsers.filter(
      (user) => user.user_email.toLowerCase() === e.target.value.toLowerCase()
    );
    setFilteredUsers(filtered);
  };

  useEffect(() => {
    fetchAllUsers();
  }, []);

  return (
    <div className="w-[60vw] mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-xl font-semibold">Add New Member</h2>
        <button onClick={onClose} className="text-gray-600">
          <X size={24} />
        </button>
      </div>
      <div className="flex-1 p-4 flex">
        <div className="w-1/2 pr-2 border-r">
          <h3 className="text-sm font-medium mb-2">Find With Email ID</h3>
          <div className="relative mb-4">
            <Search
              className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Add Mail ID"
              value={emailInput}
              onChange={handleInputChange}
              className="w-full pl-8 pr-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-600"
            />
          </div>
          {filteredUsers.length > 0 && (
            <ul className="space-y-2">
              {filteredUsers.map((user, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between bg-gray-100 rounded-md p-2 cursor-pointer"
                >
                  <div className="flex items-center">
                    <img
                      src={avatar}
                      alt="Avatar"
                      className="w-6 h-6 rounded-full mr-2"
                    />
                    <span className="text-xs truncate">{user.user_email}</span>
                  </div>
                  <button
                    onClick={() => addMemberToOrganization(user)}
                    className="bg-gray-200 text-gray-700 rounded-md p-1 hover:bg-gray-300"
                  >
                    <Plus size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="w-1/2 pl-2">
          <h3 className="text-sm font-medium mb-2">Added Members</h3>
          <ul className="space-y-2 overflow-y-auto ">
            {addedMembers.map((member, index) => (
              <li
                key={index}
                className="flex items-center justify-between bg-gray-100 rounded-md p-1"
              >
                <div className="flex items-center">
                  <img
                    src={member?.user?.userpic || avatar}
                    alt="Avatar"
                    className="w-6 h-6 rounded-full mr-2"
                  />
                  <div className="" style={{ width: "65%" }}>
                    <p className="text-xs font-medium">
                      {member.user?.first_name}
                    </p>
                    <p className="text-xs text-gray-600">
                      {member.user?.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <button className="bg-[#3B0069] text-white text-xs font-bold px-2 py-1 rounded-full">
                    Added
                  </button>
                  <button
                    onClick={() => handleDeleteClick(member)}
                    style={{
                      display: member?.joined_as === "owner" ? "none" : "block",
                    }}
                    className="bg-red-100 text-red-600 rounded-full p-1 hover:bg-red-200 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              Confirm Member Removal
            </h3>
            <p className="mb-6">
              Are you sure you want to remove {memberToDelete?.user?.first_name}{" "}
              from this organization? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 rounded-md hover:bg-red-700 text-white"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50">
          <span>Loading...</span>
        </div>
      )}
    </div>
  );
};

export default AddNewMember;
